import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  LayoutDashboard,
  Award,
  ShoppingBag,
  User,
  Share2,
  Copy as CopyIcon,
  Video,
  Calendar,
  Package,
  Receipt,
  MessageCircle,
} from "lucide-react";
import { api, LOGO, getUser, logout, rupiah } from "../lib/api";
import { CourseCard, Empty } from "../components/Shell";

export const DashSide = (p) => Sidebar(p);
function Sidebar({ active = "dashboard" }) {
  const nav = useNavigate();
  const user = getUser();
  const items = [
    { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={18} />, to: "/dashboard" },
    { key: "courses", label: "Kursus saya", icon: <BookOpen size={18} />, to: "/dashboard" },
    { key: "mentoring", label: "Mentoring Privat", icon: <Video size={18} />, to: "/mentoring" },
    { key: "store-orders", label: "Toko saya", icon: <Package size={18} />, to: "/dashboard/store" },
    { key: "certificates", label: "Sertifikat", icon: <Award size={18} />, to: "/certificates" },
    { key: "referral", label: "Referral", icon: <Share2 size={18} />, to: "/referral" },
    { key: "orders", label: "Transaksi", icon: <ShoppingBag size={18} />, to: "/orders" },
    { key: "billing", label: "Billing / Invoice", icon: <Receipt size={18} />, to: "/dashboard/billing" },
    { key: "profile", label: "Profil", icon: <User size={18} />, to: "/profile" },
  ];
  return (
    <aside className="side">
      <Link to="/" className="brand side-brand">
        <img src={LOGO} alt="MASTOFA" />
        <span>MASTOFA.COM</span>
      </Link>
      <div className="side-nav">
        <span className="side-label">RUANG BELAJAR</span>
        {items.map((i) => (
          <Link
            key={i.key}
            to={i.to}
            className={i.key === active ? "active" : ""}
            data-testid={`side-${i.key}-link`}
          >
            {i.icon} {i.label}
          </Link>
        ))}
      </div>
      <button className="logout" data-testid="logout-button" onClick={logout}>
        Keluar
      </button>
    </aside>
  );
}

export function Dashboard() {
  const user = getUser();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [nextAction, setNextAction] = useState(null);
  useEffect(() => {
    api("/me/courses")
      .then(setItems)
      .catch((e) => setError(e.response?.data?.detail || "Kursus belum dapat dimuat."));
    api("/me/next-action").then(setNextAction).catch(() => {});
  }, []);
  const avgProgress = items.length
    ? Math.round(items.reduce((s, c) => s + (c.progress || 0), 0) / items.length)
    : 0;
  const buildResumeUrl = () => {
    if (!nextAction) return "/belajar";
    if (nextAction.kind === "EXPLORE") return nextAction.target_url || "/belajar";
    if (nextAction.lesson_id && nextAction.course_slug) {
      // Match App.js route /learn/:slug/:lessonId (NO extra '/lesson/' segment)
      return `/learn/${nextAction.course_slug}/${nextAction.lesson_id}`;
    }
    if (nextAction.course_slug) return `/courses/${nextAction.course_slug}`;
    return "/belajar";
  };
  return (
    <div className="dashboard">
      <Sidebar active="dashboard" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">DASHBOARD MEMBER</span>
            <h1>
              Selamat datang kembali, <em>{user.name || "Learner"}.</em>
            </h1>
          </div>
          <div className="avatar" data-testid="member-avatar">
            {(user.name || "M")[0]}
          </div>
        </div>

        {/* Sprint 3: ACTION-FIRST — Card LANJUTKAN BELAJAR paling atas */}
        {nextAction && (
          <div
            data-testid="dashboard-next-action"
            style={{
              background: nextAction.kind === "RESUME"
                ? "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)"
                : nextAction.kind === "START"
                ? "linear-gradient(135deg,#065f46,#10b981)"
                : nextAction.kind === "NEXT_LESSON"
                ? "linear-gradient(135deg,#7c2d12,#f97316)"
                : "linear-gradient(135deg,#4c1d95,#8b5cf6)",
              color: "#fff",
              padding: "32px 28px",
              borderRadius: 16,
              marginBottom: 24,
              boxShadow: "0 10px 30px rgba(15,23,42,0.15)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
              <div style={{ minWidth: 260, flex: 1 }}>
                <div style={{ fontSize: 12, opacity: 0.85, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                  {nextAction.kind === "RESUME" && "🎯 LANJUTKAN BELAJAR"}
                  {nextAction.kind === "START" && "🚀 MULAI KURSUS"}
                  {nextAction.kind === "NEXT_LESSON" && "✨ MATERI BERIKUTNYA"}
                  {nextAction.kind === "RECOMMEND" && "💡 REKOMENDASI"}
                  {nextAction.kind === "EXPLORE" && "🧭 JELAJAHI"}
                </div>
                <h2 style={{ color: "#fff", fontSize: "1.6rem", margin: "4px 0 8px", lineHeight: 1.2 }}>
                  {nextAction.lesson_title || nextAction.course_title || "Mulai perjalanan belajar Anda"}
                </h2>
                {nextAction.course_title && nextAction.lesson_title && (
                  <small style={{ opacity: 0.8, display: "block", marginBottom: 12 }}>
                    Kursus: {nextAction.course_title}
                    {nextAction.duration ? ` · ${nextAction.duration}` : ""}
                  </small>
                )}
                {typeof nextAction.progress_percent === "number" && nextAction.kind !== "EXPLORE" && (
                  <div style={{ marginTop: 8, maxWidth: 400 }}>
                    <div style={{ background: "rgba(255,255,255,0.2)", height: 6, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{
                        background: "#fbbf24", height: "100%",
                        width: `${nextAction.progress_percent}%`,
                        transition: "width 0.4s",
                      }} />
                    </div>
                    <small style={{ opacity: 0.85, marginTop: 6, display: "block" }}>
                      Progress: {nextAction.progress_percent}%
                    </small>
                  </div>
                )}
              </div>
              <Link
                to={buildResumeUrl()}
                data-testid="dashboard-resume-btn"
                style={{
                  background: "#fff",
                  color: nextAction.kind === "RESUME" ? "#1e40af"
                    : nextAction.kind === "START" ? "#065f46"
                    : nextAction.kind === "NEXT_LESSON" ? "#7c2d12" : "#4c1d95",
                  padding: "14px 26px",
                  borderRadius: 10,
                  fontWeight: 700,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "1rem",
                  whiteSpace: "nowrap",
                }}
              >
                {nextAction.cta_label} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}

        <div className="dash-stats" style={{ marginTop: 0 }}>
          <div>
            <span>KURSUS AKTIF</span>
            <strong data-testid="active-course-count">{items.length}</strong>
          </div>
          <div>
            <span>PROGRESS RATA-RATA</span>
            <strong>{`${avgProgress}%`}</strong>
          </div>
          <div>
            <span>SERTIFIKAT</span>
            <strong>{items.filter((c) => c.progress === 100).length}</strong>
          </div>
        </div>
        <section className="my-learning">
          <div className="section-head">
            <div>
              <span className="section-kicker">LANJUTKAN BELAJAR</span>
              <h2>Kursus saya</h2>
            </div>
            <Link to="/courses" className="text-link">
              Tambah kursus <ArrowRight size={16} />
            </Link>
          </div>
          {error ? (
            <div className="error" data-testid="dashboard-error">
              {error}
            </div>
          ) : items.length ? (
            <div className="course-grid">
              {items.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/learn/${c.slug}`}
                  className="course-card"
                  data-testid={`my-course-${c.id}`}
                >
                  <div className="course-image">
                    <img src={c.thumbnail} alt={c.title} />
                    <span className="progress-badge">{c.progress || 0}%</span>
                  </div>
                  <div className="course-info">
                    <div className="course-meta">
                      <span>{c.level}</span>
                      <span>Belajar</span>
                    </div>
                    <h3>{c.title}</h3>
                    <div className="progress-bar">
                      <span style={{ width: (c.progress || 0) + "%" }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <Empty
              icon={<BookOpen size={28} />}
              title="Belum ada kursus aktif"
              body="Mulai perjalanan belajar dengan memilih kelas pertama Anda."
              cta={
                <Link to="/courses" className="button" data-testid="empty-browse-button">
                  Jelajahi kursus
                </Link>
              }
            />
          )}
        </section>
      </main>
    </div>
  );
}

export function Certificates() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api("/me/certificates").then(setItems).catch(() => {});
  }, []);
  return (
    <div className="dashboard">
      <Sidebar active="certificates" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">SERTIFIKAT</span>
            <h1>Sertifikat Anda</h1>
            <p>Selesaikan semua materi dan quiz untuk menerima sertifikat.</p>
          </div>
        </div>
        {items.length ? (
          <div className="cert-grid" data-testid="cert-grid">
            {items.map((c) => (
              <Link
                key={c.id}
                to={`/certificate/${c.verification_code}`}
                className="cert-card"
                data-testid={`cert-${c.id}`}
              >
                <Award size={26} />
                <b>{c.course_title}</b>
                <p>Kode: {c.verification_code}</p>
                <small>{new Date(c.issued_at).toLocaleDateString("id-ID")}</small>
              </Link>
            ))}
          </div>
        ) : (
          <Empty
            icon={<Award size={28} />}
            title="Belum ada sertifikat"
            body="Selesaikan seluruh materi kursus untuk mendapatkan sertifikat."
          />
        )}
      </main>
    </div>
  );
}

export function Orders() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api("/me/courses").then(() => {}).catch(() => {});
  }, []);
  return (
    <div className="dashboard">
      <Sidebar active="orders" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">TRANSAKSI</span>
            <h1>Riwayat pembelian</h1>
            <p>Semua transaksi diproses aman via Duitku.</p>
          </div>
        </div>
        <Empty
          icon={<ShoppingBag size={28} />}
          title="Belum ada transaksi"
          body="Setelah menyelesaikan pembelian, transaksi Anda akan tampil di sini."
        />
      </main>
    </div>
  );
}

export function Referral() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lb, setLb] = useState(null);
  const [period, setPeriod] = useState("current_month");
  useEffect(() => {
    api("/me/referral").then(setData).catch(() => {});
  }, []);
  useEffect(() => {
    setLb(null);
    api(`/referral/leaderboard?period=${period}`).then(setLb).catch(() => setLb({ entries: [] }));
  }, [period]);
  const copy = () => {
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="dashboard">
      <Sidebar active="referral" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">PROGRAM REFERRAL</span>
            <h1>
              Ajak teman, <em>dapat 20%.</em>
            </h1>
            <p>
              Setiap teman yang beli kursus lewat link Anda, dapat komisi 20% dari nilai transaksi.
            </p>
          </div>
        </div>
        {!data ? (
          <div className="loading">Memuat...</div>
        ) : (
          <>
            <div className="dash-stats">
              <div>
                <span>KODE REFERRAL</span>
                <strong style={{ fontSize: 22 }} data-testid="ref-code">
                  {data.referral_code}
                </strong>
              </div>
              <div>
                <span>DIREFER</span>
                <strong data-testid="ref-count">{data.referred_count}</strong>
              </div>
              <div>
                <span>KOMISI TOTAL</span>
                <strong data-testid="ref-earned">{rupiah(data.total_earned)}</strong>
              </div>
              <div>
                <span>BELUM CAIR</span>
                <strong style={{ color: "#f59e0b" }} data-testid="ref-pending">
                  {rupiah(data.pending)}
                </strong>
              </div>
            </div>
            <div className="referral-share" data-testid="referral-share">
              <b>Link referral Anda</b>
              <div className="share-row">
                <input readOnly value={data.referral_link} data-testid="ref-link-input" />
                <button className="button" onClick={copy} data-testid="ref-copy-button">
                  <CopyIcon size={15} /> {copied ? "Tersalin!" : "Copy"}
                </button>
              </div>
              <div className="share-actions">
                <a
                  className="button-ghost"
                  data-testid="ref-wa-button"
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `Halo! Aku belajar digital marketing di MASTOFA.COM. Pakai link ini biar dapat konten praktis: ${data.referral_link}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Share via WhatsApp
                </a>
              </div>
            </div>
            <section className="my-learning">
              <div className="section-head">
                <div>
                  <span className="section-kicker">RIWAYAT KOMISI</span>
                  <h2>Detail transaksi</h2>
                </div>
              </div>
              <div className="admin-table">
                <div className="table">
                  {data.commissions.length === 0 && (
                    <div className="empty-inline">Belum ada komisi masuk.</div>
                  )}
                  {data.commissions.map((c) => (
                    <div key={c.id} className="table-row commission-row" data-testid={`comm-${c.id}`}>
                      <div className="table-course">
                        <div>
                          <b>{c.product_title}</b>
                          <span>{c.buyer_email}</span>
                        </div>
                      </div>
                      <span className={`status ${c.status === "PAID_OUT" ? "published" : "pending"}`}>
                        {c.status}
                      </span>
                      <span>{rupiah(c.commission)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            <section className="my-learning leaderboard-section" data-testid="referral-leaderboard">
              <div className="section-head">
                <div>
                  <span className="section-kicker">LEADERBOARD</span>
                  <h2>Top referrer</h2>
                  {lb?.period_label && <p className="muted">{lb.period_label}</p>}
                </div>
                <div className="lb-period-tabs" data-testid="lb-period-tabs">
                  <button
                    className={period === "current_month" ? "chip active" : "chip"}
                    onClick={() => setPeriod("current_month")}
                    data-testid="lb-period-current"
                  >
                    Bulan ini
                  </button>
                  <button
                    className={period === "last_month" ? "chip active" : "chip"}
                    onClick={() => setPeriod("last_month")}
                    data-testid="lb-period-last"
                  >
                    Bulan lalu
                  </button>
                  <button
                    className={period === "all_time" ? "chip active" : "chip"}
                    onClick={() => setPeriod("all_time")}
                    data-testid="lb-period-all"
                  >
                    Semua
                  </button>
                </div>
              </div>
              {!lb ? (
                <div className="loading">Memuat leaderboard...</div>
              ) : lb.entries.length === 0 ? (
                <div className="empty-inline" data-testid="lb-empty">
                  Belum ada referrer di periode ini. Jadi orang pertama! Bagikan link Anda sekarang.
                </div>
              ) : (
                <div className="lb-list" data-testid="lb-list">
                  {lb.entries.map((e) => (
                    <div
                      key={`${e.rank}-${e.masked_email}`}
                      className={e.is_you ? "lb-row is-you" : "lb-row"}
                      data-testid={`lb-row-${e.rank}`}
                    >
                      <span className={`lb-rank rank-${Math.min(e.rank, 4)}`}>
                        {e.rank <= 3 ? ["🥇", "🥈", "🥉"][e.rank - 1] : `#${e.rank}`}
                      </span>
                      <div className="lb-user">
                        <b>{e.name}{e.is_you && <span className="you-badge"> · Anda</span>}</b>
                        <small>{e.masked_email}</small>
                      </div>
                      <div className="lb-stat">
                        <b>{rupiah(e.total_commission)}</b>
                        <small>{e.orders} order · {e.unique_buyers} buyer</small>
                      </div>
                    </div>
                  ))}
                  {lb.your_rank && (
                    <div className="lb-row your-rank-row" data-testid="lb-your-rank">
                      <span className="lb-rank">#{lb.your_rank.rank}</span>
                      <div className="lb-user">
                        <b>Anda</b>
                        <small>Naik ke top 20 dengan {rupiah((lb.entries[lb.entries.length-1]?.total_commission || 0) + 1 - lb.your_rank.total_commission)} lagi</small>
                      </div>
                      <div className="lb-stat">
                        <b>{rupiah(lb.your_rank.total_commission)}</b>
                        <small>total komisi</small>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export function Profile() {
  const user = getUser();
  const [form, setForm] = useState({ name: user?.name || "", whatsapp: user?.whatsapp || "" });
  const [pwForm, setPwForm] = useState({ current: "", new_password: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("/auth/me").then(({ user: u }) => {
      setForm({ name: u.name || "", whatsapp: u.whatsapp || "" });
      localStorage.setItem("mastofa_user", JSON.stringify(u));
    }).catch(() => {});
  }, []);
  const saveProfile = async () => {
    setBusy(true);
    try {
      const r = await api("/auth/me", { method: "PATCH", data: form });
      localStorage.setItem("mastofa_user", JSON.stringify(r));
      setMsg("Profil tersimpan.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal simpan profil.");
    } finally {
      setBusy(false);
    }
  };
  const savePassword = async () => {
    if (pwForm.new_password.length < 8) return setMsg("Password minimal 8 karakter.");
    setBusy(true);
    try {
      await api("/auth/change-password", { method: "POST", data: pwForm });
      setPwForm({ current: "", new_password: "" });
      setMsg("Password berhasil diganti.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal ganti password.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dashboard">
      <Sidebar active="profile" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">PROFIL</span>
            <h1>Akun Anda</h1>
          </div>
        </div>
        {msg && <div className="notice" data-testid="profile-msg">{msg}</div>}
        <div className="profile-card" data-testid="profile-card">
          <div><span>EMAIL</span><b>{user.email}</b></div>
          <div><span>PERAN</span><b>{user.role}</b></div>
        </div>
        <div className="admin-editor" style={{ marginTop: 20 }}>
          <h3 style={{ font: "800 16px Manrope" }}>Data Diri</h3>
          <label>
            Nama
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </label>
          <label>
            Nomor WhatsApp
            <input
              type="tel"
              placeholder="628123456789"
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/\D/g, "") })}
              data-testid="profile-whatsapp"
            />
            <small style={{ color: "#94a3b8" }}>Untuk konfirmasi jadwal mentoring, resi, dan info penting.</small>
          </label>
          <button className="button" onClick={saveProfile} disabled={busy} data-testid="save-profile">
            {busy ? "Menyimpan..." : "Simpan Profil"}
          </button>
        </div>
        <div className="admin-editor" style={{ marginTop: 20 }}>
          <h3 style={{ font: "800 16px Manrope" }}>Ganti Password</h3>
          <label>
            Password saat ini
            <input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="profile-pw-current" />
          </label>
          <label>
            Password baru (min 8 karakter)
            <input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="profile-pw-new" />
          </label>
          <button className="button" onClick={savePassword} disabled={busy} data-testid="save-password">
            {busy ? "Menyimpan..." : "Ganti Password"}
          </button>
        </div>
      </main>
    </div>
  );
}

export function CertificateVerify() {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api(`/certificates/${code}`)
      .then(setCert)
      .catch((e) => setErr(e.response?.data?.detail || "Sertifikat tidak ditemukan."));
  }, [code]);
  if (err)
    return (
      <div className="certificate-page">
        <div className="cert-box">
          <b>{err}</b>
        </div>
      </div>
    );
  if (!cert) return <div className="loading">Memverifikasi...</div>;
  return (
    <div className="certificate-page" data-testid="certificate-verify">
      <div className="cert-box">
        <img src={LOGO} alt="MASTOFA.COM" />
        <span className="section-kicker">SERTIFIKAT MASTOFA</span>
        <h1>{cert.user_name}</h1>
        <p>telah menyelesaikan kursus</p>
        <h2>{cert.course_title}</h2>
        <div className="cert-meta">
          <span>Kode verifikasi</span>
          <b>{cert.verification_code}</b>
        </div>
        <small>
          Diterbitkan {new Date(cert.issued_at).toLocaleDateString("id-ID")} · MASTOFA.COM
        </small>
      </div>
    </div>
  );
}

export function Mentoring() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => api("/me/mentoring").then(setItems).catch(() => {});
  useEffect(() => {
    load();
    api("/mentoring/settings").then(setSettings).catch(() => {});
  }, []);
  const activeEnrollments = items.filter((e) =>
    e.status === "ACTIVE" && (e.sessions_used || 0) < (e.sessions_total || 0)
  );
  const hasUnscheduled = activeEnrollments.some((e) =>
    (e.sessions || []).some((s) => ["PENDING", "REQUESTED"].includes(s.status))
  );
  const user = (typeof localStorage !== "undefined")
    ? JSON.parse(localStorage.getItem("mastofa_user") || "{}") : {};
  const adminWa = String(settings.admin_whatsapp || "6285157009099").replace(/^\+|\D/g, "");
  const buildWaLink = (enrollment) => {
    const remaining = (enrollment.sessions_total || 0) - (enrollment.sessions_used || 0);
    const msg = `Halo Admin Mas Tofa, saya ${user?.name || "member"} (${user?.email || "-"}). Saya sudah membeli paket *${enrollment.package_title || "Privat 1-on-1"}* dan ingin *mengatur jadwal sesi* saya (sisa ${remaining} sesi). Bisa dibantu?`;
    return `https://wa.me/${adminWa}?text=${encodeURIComponent(msg)}`;
  };
  const request = async (enrollmentId, sessionId, current) => {
    const topic = window.prompt("Topik sesi (mis. 'Bedah campaign Shopee A/B'):", current?.topic || "");
    if (topic === null) return;
    const preferred = window.prompt("Tanggal & jam preferensi (mis. 'Sabtu 12 Apr 19:00'):", current?.preferred_date || "");
    setBusy(true);
    try {
      await api(`/me/mentoring/${enrollmentId}/session/${sessionId}/request`, {
        method: "POST",
        data: { topic: topic || "", preferred_date: preferred || "", notes: "" },
      });
      setMsg("Request sesi terkirim. Admin akan konfirmasi Zoom link.");
      load();
    } catch (e) {
      setMsg(e?.message || "Gagal request sesi.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dashboard">
      <Sidebar active="mentoring" />
      <main className="dash-main">
        <header className="dash-header">
          <span className="section-kicker">MENTORING PRIVAT</span>
          <h1>Sesi 1-on-1 Anda</h1>
          <p>Request jadwal sesi, lihat Zoom link, dan pantau progress sesi Anda.</p>
        </header>
        {msg && <div className="notice" data-testid="mentoring-msg">{msg}</div>}
        {activeEnrollments.length > 0 && hasUnscheduled && (
          <div
            data-testid="mentoring-schedule-banner"
            style={{
              background: "linear-gradient(135deg,#065f46,#10b981)",
              color: "#fff",
              padding: 24,
              borderRadius: 12,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              boxShadow: "0 8px 24px rgba(16,185,129,0.25)",
            }}
          >
            <div style={{ minWidth: 240, flex: 1 }}>
              <div style={{ fontSize: 13, opacity: 0.9, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                🎉 Pembayaran Terkonfirmasi
              </div>
              <div style={{ fontSize: "1.35rem", fontWeight: 700, marginBottom: 6 }}>
                Yuk atur jadwal sesi Privat 1-on-1 Anda!
              </div>
              <small style={{ opacity: 0.85, lineHeight: 1.5, display: "block" }}>
                Jadwal fleksibel — chat langsung Admin via WhatsApp untuk booking hari & jam Anda.
              </small>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {activeEnrollments.map((e) => (
                <a
                  key={e.id}
                  href={buildWaLink(e)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`mentoring-wa-${e.id}`}
                  className="button"
                  style={{
                    background: "#fff",
                    color: "#065f46",
                    fontWeight: 700,
                    padding: "12px 22px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    borderRadius: 8,
                  }}
                >
                  <MessageCircle size={18} /> Chat Admin — {e.package_title || "Booking Jadwal"}
                </a>
              ))}
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <Empty
            icon={<Video size={38} />}
            title="Belum ada paket mentoring"
            body="Pilih paket privat 1-on-1 dengan Mas Tofa untuk mulai."
            cta={<Link to="/privat" className="button">Lihat paket privat <ArrowRight size={16} /></Link>}
          />
        ) : (
          <div className="mentoring-list" data-testid="mentoring-list">
            {items.map((e) => (
              <div key={e.id} className="mentoring-card" data-testid={`mentoring-${e.id}`}>
                <div className="mentoring-head">
                  <div>
                    <h3>{e.package_title}</h3>
                    <small>
                      Mode: {e.mode} · Extra peserta: {e.extra_participants || 0} · WA: {e.whatsapp || "-"}
                    </small>
                  </div>
                  <div className="mentoring-progress">
                    <b>{e.sessions_used} / {e.sessions_total}</b>
                    <small>sesi terpakai</small>
                  </div>
                </div>
                <div className="mentoring-sessions">
                  {(e.sessions || []).map((s) => (
                    <div key={s.id} className={`session-row status-${s.status}`} data-testid={`session-${s.id}`}>
                      <div>
                        <b>Sesi #{s.index}</b>
                        <span className="badge">{s.status}</span>
                      </div>
                      <div className="session-meta">
                        {s.scheduled_at && (
                          <div><Calendar size={13} /> {s.scheduled_at}</div>
                        )}
                        {s.zoom_link && (
                          <a href={s.zoom_link} target="_blank" rel="noreferrer" data-testid={`zoom-${s.id}`}>
                            <Video size={13} /> Buka Zoom
                          </a>
                        )}
                        {s.topic && <small>Topik: {s.topic}</small>}
                        {s.preferred_date && !s.scheduled_at && (
                          <small>Preferensi kamu: {s.preferred_date}</small>
                        )}
                      </div>
                      {["PENDING", "REQUESTED"].includes(s.status) && (
                        <button
                          className="button button-small"
                          onClick={() => request(e.id, s.id, s)}
                          disabled={busy}
                          data-testid={`req-${s.id}`}
                        >
                          {s.status === "REQUESTED" ? "Update request" : "Request jadwal"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

